import { useMemo, useState } from "react";
import {
  ALWAYS_ON,
  blockedBy,
  FEATURE_AREAS,
  FEATURE_PRESETS,
  FEATURES,
  featureDef,
  resolveFeatures,
  type FeatureId,
  type FeaturePresetId,
  type FeatureSwitches,
} from "@baros/core";
import type { OrgFeatures, PlatformOrg } from "./api.js";

/**
 * One organisation's features (D14), for a Venox operator: a preset to apply
 * in one go, or individual switches staged and saved together. A feature
 * whose parent or requirement is off says so instead of pretending to be on.
 *
 * Nothing here deletes anything. Turning a feature off hides it from the
 * organisation and stops its jobs; its data waits for it to come back.
 */
export function FeaturesPanel(props: {
  readonly org: PlatformOrg;
  readonly busy: boolean;
  readonly onSave: (body: { preset?: FeaturePresetId; features?: FeatureSwitches }, note: string) => Promise<void>;
}) {
  const current: OrgFeatures = props.org.features ?? { switches: {}, enabled: FEATURES.map((f) => f.id), preset: "full" };
  const [staged, setStaged] = useState<FeatureSwitches>({});

  const effective = useMemo<FeatureSwitches>(() => ({ ...current.switches, ...staged }), [current.switches, staged]);
  const enabled = useMemo(() => resolveFeatures(effective), [effective]);
  const changed = (Object.keys(staged) as FeatureId[]).filter(
    (id) => (staged[id] ?? true) !== (current.switches[id] ?? true),
  );
  const presetLabel = (id: FeaturePresetId | null) => FEATURE_PRESETS.find((p) => p.id === id)?.label ?? "Custom";

  return (
    <div className="pf-features">
      <div className="pf-history-head">
        <span className="label">
          {props.org.name}'s features · {presetLabel(current.preset)} · {current.enabled.length} of {FEATURES.length} on
        </span>
      </div>

      <div className="pf-presets">
        {FEATURE_PRESETS.map((p) => (
          <button
            key={p.id}
            className="pf-preset"
            aria-pressed={current.preset === p.id && changed.length === 0}
            disabled={props.busy}
            onClick={() => {
              if (!window.confirm(`Set ${props.org.name} to ${p.label}? ${p.description} Nothing is deleted.`)) return;
              void props.onSave({ preset: p.id }, `${props.org.name} is on ${p.label}.`).then(() => setStaged({}));
            }}
          >
            <span className="n">{p.label}</span>
            <span className="s">{p.description}</span>
          </button>
        ))}
      </div>

      <div className="pf-feature-areas">
        {FEATURE_AREAS.map((area) => {
          const features = FEATURES.filter((f) => f.area === area.id);
          if (features.length === 0) return null;
          return (
            <div key={area.id} className="pf-feature-area">
              <div className="label">{area.label}</div>
              {features.map((f) => {
                const switchedOn = effective[f.id] !== false;
                const blocked = switchedOn ? blockedBy(f.id, enabled) : [];
                return (
                  <label key={f.id} className={`pf-feature${"parent" in f ? " sub" : ""}`}>
                    <input
                      type="checkbox"
                      checked={switchedOn}
                      disabled={props.busy}
                      onChange={(e) => setStaged({ ...staged, [f.id]: e.target.checked })}
                    />
                    <span className="pf-feature-text">
                      <span className="n">
                        {f.label}
                        {changed.includes(f.id) && <span className="pf-changed"> · changed</span>}
                      </span>
                      <span className="s">
                        {blocked.length > 0
                          ? `Off until ${blocked.map((b) => featureDef(b).label).join(" and ")} ${blocked.length === 1 ? "is" : "are"} on.`
                          : f.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          );
        })}
        <div className="pf-feature-area">
          <div className="label">Always on</div>
          {ALWAYS_ON.map((name) => (
            <div key={name} className="pf-feature fixed">
              <input type="checkbox" checked disabled readOnly />
              <span className="pf-feature-text">
                <span className="n">{name}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="row" style={{ gap: 10, marginTop: 12 }}>
        <button
          className="btn primary"
          disabled={props.busy || changed.length === 0}
          onClick={() => {
            const patch = Object.fromEntries(changed.map((id) => [id, staged[id]])) as FeatureSwitches;
            const offs = changed.filter((id) => staged[id] === false).map((id) => featureDef(id).label);
            if (offs.length && !window.confirm(`Turn off ${offs.join(", ")} for ${props.org.name}? It disappears for them at once; nothing is deleted.`)) return;
            void props
              .onSave({ features: patch }, `${props.org.name}'s features are saved (${changed.length} change${changed.length === 1 ? "" : "s"}).`)
              .then(() => setStaged({}));
          }}
        >
          Save {changed.length > 0 ? `${changed.length} change${changed.length === 1 ? "" : "s"}` : "changes"}
        </button>
        {changed.length > 0 && (
          <button className="btn" disabled={props.busy} onClick={() => setStaged({})}>
            Discard
          </button>
        )}
        <span className="spacer" />
        <span className="label" style={{ letterSpacing: "0.06em" }}>
          Takes effect at once here, within a minute everywhere. Recorded in their activity log.
        </span>
      </div>
    </div>
  );
}
